import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { SEO } from './components/SEO';
import { generateHomePageStructuredData, generateOrganizationStructuredData } from './utils/structuredData';
import { UpgradeToPremium } from './pages/UpgradeToPremium';
import { Header } from './components/Header';
import { SearchResults } from './components/SearchResults';
import { Footer } from './components/Footer';
import { SupabaseSearchProvider } from './context/SupabaseSearchContext';
import { ViewModeProvider } from './context/ViewModeContext';
import { SavedFoundationsProvider } from './context/SavedFoundationsContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { SponsoredLinksProvider } from './context/SponsoredLinksContext';
import { TopHeader } from './components/TopHeader';
import { GDPRInfo } from './components/GDPRInfo';
import { BlogFeed } from './components/BlogFeed';
import { PaymentConfirmation } from './pages/PaymentConfirmation';
import Dashboard from './pages/Dashboard';
import { CreateFoundation } from './pages/CreateFoundation';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { AlltOmStiftelser } from './pages/AlltOmStiftelser'; // Component name kept the same for simplicity
import { Projektborsen } from './pages/Projektborsen';
import { VanligaFragor } from './pages/VanligaFragor';
import { BillingInfo } from './pages/BillingInfo';
import { DeveloperTools } from './components/DeveloperTools';
import { DeveloperModeWarning } from './components/DeveloperModeWarning';
import { FoundationsManager } from './components/FoundationsManager';
import { AdminTools } from './components/AdminTools';
import Success from './pages/Success';

function App() {
  const location = useLocation();
  
  useEffect(() => {
    // Scroll to top when navigating to a new page (except for hash links)
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
    
    // Handle hash fragments in the URL
    if (location.hash === '#contact') {
      // Dispatch a custom event to open the contact modal
      window.dispatchEvent(new CustomEvent('openContactModal'));
    } else if (location.hash === '#search') {
      // Focus on the search bar
      const searchInput = document.querySelector('.search-input');
      if (searchInput) {
        (searchInput as HTMLInputElement).focus();
      }
      // Scroll to the search bar
      const header = document.querySelector('header');
      if (header) {
        header.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2C1D1] to-white flex flex-col overflow-x-hidden">
      {/* Development mode warning - only shown in development */}
      {process.env.NODE_ENV !== 'production' && <DeveloperModeWarning />}
      {/* Global SEO for the homepage */}
      <SEO
        title="Sök bland stiftelser i Sverige"
        description="Hitta rätt stiftelse för ditt ändamål. Sök bland över 17 000 svenska stiftelser och få tillgång till information om finansieringsmöjligheter."
        keywords="stiftelser, svenska stiftelser, stipendier, forskningsfinansiering, donationer, välgörenhet, studiebidrag, stiftelsesökning"
        structuredData={location.pathname === '/' ?
          generateHomePageStructuredData() :
          generateOrganizationStructuredData()
        }
      />
      <SubscriptionProvider>
        <SupabaseSearchProvider>
          <ViewModeProvider>
            <SponsoredLinksProvider>
              <SavedFoundationsProvider>
            <TopHeader />
            <Routes>
              <Route path="/" element={
                <>
                  <Header />
                  <SearchResults />
                  <GDPRInfo />
                </>
              } />
              <Route path="/blogg" element={<BlogFeed />} />
              <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
              <Route path="/premium" element={<UpgradeToPremium />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/starta-stiftelse" element={<CreateFoundation />} />
              <Route path="/integritetspolicy" element={<PrivacyPolicy />} />
              <Route path="/allt-om-stiftelser" element={<AlltOmStiftelser />} />
              <Route path="/projektborsen" element={<Projektborsen />} />
              <Route path="/betalningsinformation" element={<BillingInfo />} />
              <Route path="/vanliga-fragor" element={<VanligaFragor />} />
              <Route path="/success" element={<Success />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Footer />
            
            
            {/* Developer Tools - only shown in development mode */}
            {process.env.NODE_ENV !== 'production' && <DeveloperTools />}
            
            {/* Admin Tools - shown in development or when ?admin parameter is present */}
            <AdminTools />
            
            
            {/* New foundations manager */}
            <FoundationsManager />
            
            {/* Sponsored links are automatically updated every 5 minutes */}
              </SavedFoundationsProvider>
            </SponsoredLinksProvider>
          </ViewModeProvider>
        </SupabaseSearchProvider>
      </SubscriptionProvider>
    </div>
  );
}

export default App;
